import { Button } from "@/components/ui/button";
import { Check, X, DollarSign, Wrench, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FeatureMatrix } from "./FeatureMatrix";
import { SavingsCalculator } from "./SavingsCalculator";
import { Card } from "@/components/ui/card";

export const CompetitorComparison = () => {
  return (
    <section className="py-32 bg-gradient-to-b from-background via-secondary/20 to-background">
      <div className="container mx-auto px-4">
        {/* Headline Section */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
            One Platform. Everything You Need. Way Less Money.
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Stop paying for 3-4 different tools. Get everything in one place for less than the cost of a single competitor.
          </p>
        </div>

        {/* Price Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-20 max-w-5xl mx-auto">
          {/* Old Way */}
          <Card className="relative p-8 bg-gradient-to-br from-destructive/5 to-destructive/10 border-2 border-destructive/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-full bg-destructive/20 flex items-center justify-center">
                <X className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-destructive">The Old Way</h3>
                <p className="text-sm text-muted-foreground">Buying Separately</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center p-3 rounded-lg bg-background/50">
                <span className="text-sm font-medium">Teal Resume Builder</span>
                <span className="font-semibold">$36/mo</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-background/50">
                <span className="text-sm font-medium">Resume Worded (ATS + LinkedIn)</span>
                <span className="font-semibold">$49/mo</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-background/50">
                <span className="text-sm font-medium">JobCopilot (Auto-Apply)</span>
                <span className="font-semibold">$24/mo</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-background/50">
                <span className="text-sm font-medium">Interview Prep Tool</span>
                <span className="font-semibold">$20/mo</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-background/50 opacity-60">
                <span className="text-sm font-medium">Salary Negotiation</span>
                <span className="text-sm italic">Not available</span>
              </div>
            </div>

            <div className="border-t-2 border-destructive/30 pt-6 mb-6">
              <div className="flex justify-between items-center text-3xl font-bold text-destructive">
                <span>Total:</span>
                <span>$129/mo</span>
              </div>
            </div>

            <div className="text-sm text-muted-foreground bg-background/50 p-4 rounded-lg">
              Limited features, multiple logins, disconnected tools
            </div>
          </Card>

          {/* Smart Way */}
          <Card className="relative p-8 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 border-2 border-primary shadow-[var(--shadow-xl)] transform scale-105">
            <div className="absolute -top-4 right-4 bg-gradient-to-r from-primary to-accent text-primary-foreground px-4 py-2 rounded-full text-sm font-bold shadow-lg">
              Save $100/mo
            </div>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Check className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-primary">The Smart Way</h3>
                <p className="text-sm text-muted-foreground">Loopbaan.ai All-in-One</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-8">
              {[
                "AI Resume & Cover Letter Builder",
                "ATS Score Checker",
                "LinkedIn Optimizer",
                "Job Search (Free - 1 credit/page)",
                "Salary Negotiation Tools",
                "Application Tracker"
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-primary/5 transition-colors">
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <div className="border-t-2 border-primary pt-6 mb-6">
              <div className="flex justify-between items-center text-3xl font-bold text-primary">
                <span>Total:</span>
                <span>$29/mo</span>
              </div>
            </div>

            <div className="text-sm text-muted-foreground bg-background/50 p-4 rounded-lg">
              All-in-one platform, single login, seamless workflow
            </div>
          </Card>
        </div>

        {/* Social Proof Callouts */}
        <div className="grid md:grid-cols-3 gap-6 mb-20 max-w-4xl mx-auto">
          <Card className="p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            <DollarSign className="w-12 h-12 text-primary mx-auto mb-3" />
            <h4 className="font-bold text-xl mb-2">Save $1,200/year</h4>
            <p className="text-sm text-muted-foreground">
              vs buying Teal + Resume Worded + JobCopilot
            </p>
          </Card>
          <Card className="p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            <Wrench className="w-12 h-12 text-primary mx-auto mb-3" />
            <h4 className="font-bold text-xl mb-2">9 Tools in One</h4>
            <p className="text-sm text-muted-foreground">
              Stop juggling multiple subscriptions
            </p>
          </Card>
          <Card className="p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-3" />
            <h4 className="font-bold text-xl mb-2">No Feature Limits</h4>
            <p className="text-sm text-muted-foreground">
              Unlike competitors, we don't restrict core features
            </p>
          </Card>
        </div>

        {/* Feature Comparison Matrix */}
        <FeatureMatrix />

        {/* Unique Value Props */}
        <div className="grid md:grid-cols-1 gap-8 my-20 max-w-2xl mx-auto">
          <Card className="relative p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 hover:border-primary/50">
            <div className="absolute -top-3 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
              Not available anywhere else
            </div>
            <h4 className="text-xl font-bold mb-3">Salary Negotiation Tools</h4>
            <p className="text-muted-foreground leading-relaxed">
              Get paid what you're worth. Market analysis, negotiation strategy, and email templates.
            </p>
          </Card>
        </div>

        {/* Savings Calculator */}
        <SavingsCalculator />

        {/* Testimonial */}
        <div className="max-w-3xl mx-auto my-20 bg-gradient-to-br from-card to-card/50 border-2 border-primary/20 rounded-xl p-8 shadow-lg">
          <blockquote className="text-lg italic mb-6 text-foreground/90 leading-relaxed">
            "I was paying $100+/month across Teal, Resume Worded, and JobCopilot. 
            Loopbaan.ai does everything they do, plus salary negotiation tools 
            that helped me get $15K more in my offer. Easiest switch ever."
          </blockquote>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary font-bold text-lg">
              MK
            </div>
            <div>
              <p className="font-semibold text-lg">Michael Kim</p>
              <p className="text-sm text-muted-foreground">Software Engineer</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 rounded-2xl p-12 max-w-4xl mx-auto border-2 border-primary/20 shadow-xl">
          <h3 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
            Ready to Save $100/Month?
          </h3>
          <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            Join thousands of job seekers who ditched expensive tools for Loopbaan.ai
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="text-lg gap-2 shadow-lg hover:shadow-xl transition-all">
              <Link to="/signup">
                Start Free - 10 Credits
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg border-2 hover:border-primary transition-all">
              <Link to="/pricing">View Pricing</Link>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            No credit card required. Upgrade anytime.
          </p>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center mt-12">
          *Competitor pricing as of January 2025, may vary. Pricing shown is for comparable feature sets.
        </p>
      </div>
    </section>
  );
};
